import React, { createContext, useContext, useState } from 'react';
import { supabase, safeCreateSupabase } from '../lib/supabase';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'user' | 'admin';
  title?: string;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  register: (name: string, email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
  error: string;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [fatalConfigError, setFatalConfigError] = useState(false);

  // Vérifier la configuration Supabase au démarrage et restaurer la session
  React.useEffect(() => {
    if (!supabase) {
      console.error('[AuthContext] Supabase client non disponible');
      setFatalConfigError(true);
      setIsLoading(false);
      return;
    }

    // Restaurer la session existante
    const initSession = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('[AuthContext] Erreur récupération session:', sessionError);
          setIsLoading(false);
          return;
        }

        if (session?.user) {
          console.log('✅ Session restaurée:', session.user.email);
          const email = session.user.email || '';
          const isAdmin = email.endsWith('@memomundi.ai');
          const adminTitles: Record<string, string> = {
            'rory@memomundi.ai': 'Fondateur & Architecte en Chef',
            'teddy@memomundi.ai': 'Co-Fondateur & Directeur Innovation'
          };

          const restoredUser: User = {
            id: session.user.id,
            name: isAdmin ? `MMD ${email.split('@')[0].toUpperCase()}` : (email.split('@')[0] || 'Utilisateur'),
            email: email,
            role: isAdmin ? 'admin' : 'user',
            title: adminTitles[email]
          };

          setUser(restoredUser);
        }
      } catch (err) {
        console.error('[AuthContext] Erreur initialisation session:', err);
      } finally {
        setIsLoading(false);
      }
    };

    initSession();

    // Écouter les changements d'état d'authentification
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        const email = session.user.email || '';
        const isAdmin = email.endsWith('@memomundi.ai');
        const adminTitles: Record<string, string> = {
          'rory@memomundi.ai': 'Fondateur & Architecte en Chef',
          'teddy@memomundi.ai': 'Co-Fondateur & Directeur Innovation'
        };

        const updatedUser: User = {
          id: session.user.id,
          name: isAdmin ? `MMD ${email.split('@')[0].toUpperCase()}` : (email.split('@')[0] || 'Utilisateur'),
          email: email,
          role: isAdmin ? 'admin' : 'user',
          title: adminTitles[email]
        };

        setUser(updatedUser);
      } else {
        setUser(null);
      }
    });

    // Rafraîchir la session toutes les 30 minutes pour maintenir l'utilisateur connecté
    const refreshInterval = setInterval(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          await supabase.auth.refreshSession();
          console.log('🔄 Session rafraîchie automatiquement');
        }
      } catch (error) {
        console.error('Erreur rafraîchissement session:', error);
      }
    }, 30 * 60 * 1000);

    return () => {
      subscription.unsubscribe();
      clearInterval(refreshInterval);
    };
  }, []);

  const clearError = () => setError('');

  const login = async (email: string, password: string): Promise<boolean> => {
    if (!supabase) {
      setError('Service d\'authentification non disponible');
      return false;
    }
    
    setIsLoading(true);
    setError('');
    try {
      console.log('🔐 Tentative de connexion:', email);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        console.error('Erreur de connexion:', error.message);
        if (error.message.includes('Invalid login credentials')) {
          setError('Email ou mot de passe incorrect');
        } else if (error.message.includes('Email not confirmed')) {
          setError('Veuillez confirmer votre email avant de vous connecter');
        } else {
          setError(`Erreur de connexion: ${error.message}`);
        }
        return false;
      }

      if (data.user) {
        console.log('✅ Connexion réussie:', data.user.email);
        
        // Créer l'utilisateur local immédiatement
        const isAdmin = email.endsWith('@memomundi.ai');
        const adminTitles: Record<string, string> = {
          'rory@memomundi.ai': 'Fondateur & Architecte en Chef',
          'teddy@memomundi.ai': 'Co-Fondateur & Directeur Innovation'
        };

        const loggedUser: User = {
          id: data.user.id,
          name: isAdmin ? `MMD ${email.split('@')[0].toUpperCase()}` : (email.split('@')[0] || 'Utilisateur'),
          email: email,
          role: isAdmin ? 'admin' : 'user',
          title: adminTitles[email]
        };

        setUser(loggedUser);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Erreur de connexion:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (name: string, email: string, password: string): Promise<boolean> => {
    if (!supabase) {
      setError('Service d\'authentification non disponible');
      return false;
    }
    
    setIsLoading(true);
    setError('');
    try {
      console.log('📝 Tentative d\'inscription:', email);

      // Vérification du préfixe MMD
      if (name.toUpperCase().startsWith('MMD ') && !email.endsWith('@memomundi.ai')) {
        console.error('Le préfixe "MMD" est réservé aux comptes administrateurs');
        setError('Le préfixe "MMD" est réservé aux comptes administrateurs');
        return false;
      }

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: undefined,
          data: { name: name }
        }
      });

      if (error) {
        console.error('Erreur d\'inscription:', error.message);
        // Gestion des erreurs spécifiques
        if (error.message.includes('already registered') || error.message.includes('User already registered')) {
          setError('Un compte existe déjà avec cette adresse email');
        } else if (error.message.includes('Password should be at least')) {
          setError('Le mot de passe doit contenir au moins 6 caractères');
        } else if (error.message.includes('Invalid email')) {
          setError('Adresse email invalide');
        } else {
          setError(`Erreur d'inscription: ${error.message}`);
        }
        return false;
      }

      if (data.user) {
        console.log('✅ Inscription réussie:', data.user.email);
        
        // Créer le profil utilisateur
        const { error: profileError } = await supabase
          .from('users')
          .upsert([{
            id: data.user.id,
            email: data.user.email,
            created_at: new Date().toISOString()
          }]);

        if (profileError) {
          console.error('Erreur création profil:', profileError);
        }

        // Créer l'utilisateur local immédiatement
        const isAdmin = email.endsWith('@memomundi.ai');
        const adminTitles: Record<string, string> = {
          'rory@memomundi.ai': 'Fondateur & Architecte en Chef',
          'teddy@memomundi.ai': 'Co-Fondateur & Directeur Innovation'
        };

        const newUser: User = {
          id: data.user.id,
          name: isAdmin ? `MMD ${name.toUpperCase()}` : name,
          email: email,
          role: isAdmin ? 'admin' : 'user',
          title: adminTitles[email]
        };

        setUser(newUser);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Erreur d\'inscription:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    if (!supabase) {
      setUser(null);
      return;
    }
    
    try {
      console.log('🚪 Déconnexion...');
      await supabase.auth.signOut();
      setUser(null);
      console.log('✅ Déconnexion réussie');
    } catch (error) {
      console.error('Erreur de déconnexion:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, isLoading, error, clearError }}>
      {fatalConfigError ? (
        <div className="min-h-screen flex items-center justify-center p-8 text-red-700 bg-red-50">
          <div className="max-w-md text-center bg-white p-8 rounded-lg border border-red-200 shadow-lg">
            <h1 className="text-xl font-bold mb-4">⚙️ Configuration Supabase manquante</h1>
            <div className="text-left bg-red-50 p-4 rounded-lg mb-4">
              <p className="text-sm mb-2">Variables d'environnement requises :</p>
              <ul className="text-xs font-mono space-y-1">
                <li>• <code>VITE_SUPABASE_URL</code></li>
                <li>• <code>VITE_SUPABASE_ANON_KEY</code></li>
              </ul>
            </div>
            <p className="text-sm text-red-600">
              Vérifiez votre fichier <code>.env</code> et redémarrez l'application.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              Recharger
            </button>
          </div>
        </div>
      ) : children}
    </AuthContext.Provider>
  );
};